import { Zap, TrendingUp, HeartPulse, Building2, Globe, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PRESETS = [
  {
    id: 'ai_startup',
    label: 'AI Startup',
    icon: Zap,
    color: '#2867B2',
    config: {
      companySize: 'micro', industry: 'technology', infraType: 'cloud',
      approach: 'mixed', mixRatio: 30, overallMaturity: 2,
      itSystems: 5, locations: 1,
    },
  },
  {
    id: 'saas_scaleup',
    label: 'SaaS Scale-up',
    icon: TrendingUp,
    color: '#78B928',
    config: {
      companySize: 'small', industry: 'technology', infraType: 'cloud',
      approach: 'mixed', mixRatio: 50, overallMaturity: 3,
      itSystems: 15, locations: 1,
    },
  },
  {
    id: 'healthcare_org',
    label: 'Healthcare Org',
    icon: HeartPulse,
    color: '#B42318',
    config: {
      companySize: 'medium', industry: 'healthcare', infraType: 'hybrid',
      approach: 'mixed', mixRatio: 50, overallMaturity: 2,
      itSystems: 25, locations: 2,
    },
  },
  {
    id: 'finance_firm',
    label: 'Finance Firm',
    icon: Building2,
    color: '#78B928',
    config: {
      companySize: 'medium', industry: 'finance', infraType: 'onpremise',
      approach: 'consultant', mixRatio: 0, overallMaturity: 2,
      itSystems: 30, locations: 2,
    },
  },
  {
    id: 'enterprise_corp',
    label: 'Enterprise Corp',
    icon: Globe,
    color: '#2867B2',
    config: {
      companySize: 'large', industry: 'technology', infraType: 'hybrid',
      approach: 'mixed', mixRatio: 60, overallMaturity: 3,
      itSystems: 50, locations: 5,
    },
  },
  {
    id: 'government_body',
    label: 'Government Body',
    icon: Shield,
    color: '#66707A',
    config: {
      companySize: 'medium', industry: 'government', infraType: 'onpremise',
      approach: 'internal', mixRatio: 100, overallMaturity: 2,
      itSystems: 20, locations: 3,
    },
  },
];

export function PresetBar({ activePreset, onPresetSelect }) {
  const { t } = useTranslation();
  return (
    <div
      className="bg-white border border-[#DDE1E5] rounded-lg px-4 py-3 mb-6"
      data-testid="preset-bar"
      role="group"
      aria-label={t('presets.title')}
    >
      <div className="flex items-center gap-2 mb-2.5">
        <span className="text-xs font-semibold text-[#66707A] uppercase tracking-wider" id="preset-bar-label">
          {t('presets.title')}
        </span>
        {activePreset && (
          <button
            onClick={() => onPresetSelect(null)}
            className="ml-auto text-xs text-[#8A929B] hover:text-[#66707A] transition-colors"
            data-testid="preset-clear-btn"
            aria-label={t('presets.deactivate')}
          >
            {t('presets.deactivate')}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2 pb-1" aria-labelledby="preset-bar-label">
        {PRESETS.map((preset) => {
          const Icon = preset.icon;
          const isActive = activePreset === preset.id;
          return (
            <button
              key={preset.id}
              onClick={() => onPresetSelect(isActive ? null : preset.id, preset.config)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors duration-150 border ${
                isActive
                  ? 'text-white border-transparent shadow-sm'
                  : 'bg-white text-[#66707A] border-[#DDE1E5] hover:border-[#DDE1E5] hover:text-[#171A1D]'
              }`}
              style={isActive ? { backgroundColor: 'var(--carbon-800)', borderColor: 'var(--carbon-800)' } : {}}
              data-testid={`preset-${preset.id}`}
              aria-pressed={isActive}
              aria-label={`${isActive ? t('presets.deactivate') : t('presets.apply')} ${t('presets.' + preset.id, preset.label)} ${t('presets.presetLabel')}`}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              {t('presets.' + preset.id, preset.label)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
